// covered-by: frontends/carrie/src/components/ask-puntt-thread/__tests__/AskPunttThread.test.tsx
import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";
import { type ReactNode, useMemo, useRef } from "react";

import {
  isEmptyAssistantPlaceholder,
  shouldHideEmptyAssistantPlaceholder,
} from "./threadVisibility";

import { isAwaitingReviewScope } from "../../ask-v2/review/awaitingReviewScope";
import { type ChatFormDisabledReason } from "../../routes/ask-puntt/agentComponents/ChatAtoms";
import {
  isFeedMessageFromViewer,
  isHiddenMessage,
  resolveFeedActorForUserMessage,
} from "../../routes/ask-puntt/chatUtils";
import { ChatMessage } from "../../routes/ask-puntt/components/ChatMessage";
import { DayDivider } from "../../routes/ask-puntt/components/DayDivider";
import { FeedCompactLayoutProvider } from "../../routes/ask-puntt/components/FeedCompactLayoutContext";
import { PunttAvatar } from "../../routes/ask-puntt/components/PunttAvatar";
import { type QuestionAttachment } from "../../routes/ask-puntt/components/QuestionForm";
import {
  answersQuestionForm,
  questionFormPromptsFromMessage,
} from "../../routes/ask-puntt/components/QuestionFormAnswer";
import { ThinkingState } from "../../routes/ask-puntt/components/ThinkingState";
import { useContainerWidth } from "../../routes/ask-puntt/hooks/useContainerWidth";
import {
  ThreadCitationNumbersContext,
  useThreadCitationNumbers,
} from "../../routes/ask-puntt/utils/focusedCitedClaims";
import {
  FEED_COMPACT_AUTHOR_MAX_WIDTH_PX,
  feedMessageAuthorKey,
  isFeedAuthorGroupStart,
} from "../../routes/ask-puntt/utils/messageAuthorGrouping";
import {
  calendarDayKey,
  formatDayDivider,
} from "../../routes/ask-puntt/utils/messageDateGrouping";
import { AVATAR_SIZE_BIG } from "../AvatarWithInitials";

export type AskPunttThreadPresentation = "chat" | "feed";

/** The last row in `messages` that is a person's turn, or undefined. */
function findLastUserMessage(
  messages: AskPunttMessage[],
): AskPunttMessage | undefined {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.role === "user") return message;
  }
  return undefined;
}

export type AskPunttThreadFeedMember = {
  _id: string;
  name?: string;
  email?: string;
  avatar?: string | null;
};

export type AskPunttThreadProps = {
  messages: AskPunttMessage[];
  presentation?: AskPunttThreadPresentation;
  chatId?: string;
  waitingForAI?: boolean;
  feedActorName?: string;
  feedActorAvatar?: string | null;
  feedMembers?: AskPunttThreadFeedMember[];
  currentUserId?: string;
  chatOwnerId?: string;
  renderAfterMessage?: (message: AskPunttMessage) => ReactNode;
  renderBeforeMessage?: (message: AskPunttMessage) => ReactNode;
  /** Suppress assistant thinking chrome (e.g. aborted V2 transport rows). */
  hideAgentChromeForMessage?: (message: AskPunttMessage) => boolean;
  /**
   * Sends the person's next message, in words. When set, a form the agent
   * drew on the LAST row (`message.components`) takes an answer; every
   * earlier form is read-only history. Unset, every form is read-only.
   */
  onSubmitAnswers?: (
    formattedAnswers: string,
    attachments?: QuestionAttachment[],
  ) => void;
  /**
   * Locks a form's controls while the transport refuses its answer, including
   * an in-flight turn or an attachment that is not ready to send.
   */
  formDisabled?: boolean;
  /** Why the active form is locked, so its notice points to the right action. */
  formDisabledReason?: ChatFormDisabledReason;
  /** Ready files attached at the chat level for the active question form. */
  chatLevelFileCount?: number;
};

export function AskPunttThread({
  messages,
  presentation = "chat",
  chatId,
  waitingForAI = false,
  feedActorName,
  feedActorAvatar,
  feedMembers,
  currentUserId,
  chatOwnerId,
  renderAfterMessage,
  renderBeforeMessage,
  hideAgentChromeForMessage,
  onSubmitAnswers,
  formDisabled = false,
  formDisabledReason,
  chatLevelFileCount = 0,
}: AskPunttThreadProps) {
  const columnRef = useRef<HTMLDivElement>(null);
  const columnWidth = useContainerWidth(columnRef, presentation === "feed");
  const feedCompactAuthors =
    presentation === "feed" &&
    columnWidth != null &&
    columnWidth <= FEED_COMPACT_AUTHOR_MAX_WIDTH_PX;

  const visibleMessages = useMemo(
    () => messages.filter((message) => !isHiddenMessage(message)),
    [messages],
  );
  // why all messages, not the visible ones: the Context rail numbers its cards
  // from the whole history (`threadCitationNumbers`), and a badge must match its card.
  const citationNumbers = useThreadCitationNumbers(messages);

  // why: an aborted transport row (hideAgentChromeForMessage === true) is a
  // terminal state, not a live pre-stream placeholder. Keep it visible even
  // when empty so its "Stopped" affordance (renderAfterMessage) survives —
  // otherwise feed always, and chat when not-last, drops the row silently.
  const isHiddenEmptyPlaceholder = (
    message: AskPunttMessage,
    isLast: boolean,
  ): boolean => {
    if (hideAgentChromeForMessage?.(message)) return false;
    return shouldHideEmptyAssistantPlaceholder({
      message,
      isLast,
      presentation,
    });
  };

  // why: a position in `visibleMessages` is not a position on screen — neighbour
  // reads must use the painted rows so an unpainted placeholder cannot sit
  // between two painted rows and change what they mean.
  const renderedMessages = useMemo(
    () =>
      visibleMessages.filter(
        (message, index) =>
          !isHiddenEmptyPlaceholder(
            message,
            index === visibleMessages.length - 1,
          ),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [presentation, visibleMessages, hideAgentChromeForMessage],
  );

  const lastRenderedMessageId =
    renderedMessages[renderedMessages.length - 1]?._id;

  const lastMsg = visibleMessages[visibleMessages.length - 1];
  // why: the row the TRANSCRIPT ends on, which is not always the last row
  // painted — see `isStreaming` below.
  const lastThreadMessageId = lastMsg?._id;
  const lastIsAssistant = lastMsg?.role === "assistant";
  const showFeedPlaceholderThinking =
    presentation === "feed" &&
    waitingForAI &&
    lastIsAssistant &&
    !!lastMsg &&
    isEmptyAssistantPlaceholder(lastMsg);

  const showThinking =
    waitingForAI && (!lastIsAssistant || showFeedPlaceholderThinking);
  const awaitingScope = isAwaitingReviewScope(visibleMessages, waitingForAI);

  const now = useMemo(() => new Date(), [visibleMessages.length]);

  const rows: ReactNode[] = [];
  let prevDay: string | null = null;
  let prevVisibleFeedAuthorKey: string | null = null;
  /* The row key an ASSISTANT row is rendered under, and why it is not
     `message._id`: while a warhol turn runs, the answer is drawn on a row whose
     identity CHANGES mid-turn. `applyStreamSnapshot` synthesises one keyed by
     the snapshot address (`w<userMessageId>`) in the seconds before christo has
     written anything for the turn, and merges the live text into christo's
     placeholder — a stored id — the moment that placeholder exists. The answer
     itself then settles onto that same stored row. Three ids for one answer, so
     `key={message._id}` unmounts and remounts the bubble at the hand-off, which
     restarts every mount effect and drops a text selection the reader made
     while it streamed.

     The TURN is what is stable across all three, and the person's own message
     is the turn: the row that answers it is always the Nth assistant row of
     that turn, whichever id it happens to carry. So the key is
     `<turn message id>#<n>` — unchanged from synthesised to placeholder to
     settled. A row the turn cannot name (an assistant row with no user message
     above it, which no served thread produces) keeps its own `_id`.

     The count runs over `renderedMessages`, so a row that is not painted does
     not consume an index. */
  let turnRowKey: string | null = null;
  let turnRowSeq = 0;

  renderedMessages.forEach((message, idx) => {
    // The last row the person can SEE — not the last element of the thread.
    // A form is answerable exactly while the painted thread ends on it, so a
    // dropped placeholder trailing it must not lock it.
    const isLast = idx === renderedMessages.length - 1;

    if (message.role === "user") {
      turnRowKey = message._id;
      turnRowSeq = 0;
    } else {
      turnRowSeq += 1;
    }
    const rowKey =
      message.role === "user" || turnRowKey == null
        ? message._id
        : `${turnRowKey}#${turnRowSeq}`;

    const day = calendarDayKey(message.createdAt);
    if (day && day !== prevDay) {
      rows.push(
        <DayDivider
          key={`day-divider-${day}-${message._id}`}
          label={formatDayDivider(message.createdAt, now)}
          day={day}
          sticky={presentation === "feed"}
        />,
      );
      prevDay = day;
    }

    const feedActor =
      presentation === "feed" && message.role === "user"
        ? resolveFeedActorForUserMessage(message, {
            currentUserId,
            chatOwnerId,
            members: feedMembers,
            viewerName: feedActorName,
            viewerAvatar: feedActorAvatar,
          })
        : null;

    const feedAuthorKey =
      presentation === "feed" ? feedMessageAuthorKey(message) : null;
    const showFeedAuthorHeader =
      feedAuthorKey == null ||
      !feedCompactAuthors ||
      isFeedAuthorGroupStart(prevVisibleFeedAuthorKey, feedAuthorKey);
    if (feedAuthorKey != null) {
      prevVisibleFeedAuthorKey = feedAuthorKey;
    }

    // why: `isLast` is the last painted row, which is the right gate for the
    // answerable form. Streaming stays on the TRANSCRIPT's last row. Deriving
    // it from `isLast` would flag a finished answer as in flight when only an
    // unpainted placeholder trails it, and ChatMessage would hide its thumbs.
    const isStreaming =
      waitingForAI &&
      message._id === lastThreadMessageId &&
      message.role === "assistant";
    const isUserMessage = message.role === "user";
    const previousMessage = idx > 0 ? renderedMessages[idx - 1] : undefined;
    const questionFormPrompts = isUserMessage
      ? questionFormPromptsFromMessage(previousMessage)
      : [];
    const isQuestionFormAnswer =
      isUserMessage &&
      answersQuestionForm(message.content ?? "", questionFormPrompts);
    const nextMessage = renderedMessages[idx + 1];
    const hideAnsweredQuestionForm =
      !isUserMessage &&
      nextMessage?.role === "user" &&
      answersQuestionForm(
        nextMessage.content ?? "",
        questionFormPromptsFromMessage(message),
      );

    // Parity with V1 chat.tsx: while the last assistant turn streams inline, its
    // ThinkingState must reflect the preceding USER turn's attachments so the
    // pre-agent read pause shows "Reading attachments..." instead of the generic
    // "Starting up...". Without forwarding this, the shared thread always lost
    // that phase in chat presentation. Read the previous PAINTED row: an orphan
    // placeholder sitting between the user turn and the live one is invisible,
    // and carries no attachments, so counting it would swallow the phase.
    const inlinePrecedingUserHasAttachments =
      isStreaming && !!renderedMessages[idx - 1]?.attachments?.length;

    rows.push(
      // why: `useReadingMessage` finds the answer the reader is on by the
      // data attributes, so the v2 rail can show that answer's citations.
      // They carry the row's CURRENT stored id, which is what the rail looks
      // the message up by; `rowKey` names the turn instead, so the bubble is
      // not remounted when that id changes mid-stream.
      <div
        key={rowKey}
        data-message-id={message._id}
        data-message-role={message.role}
      >
        <ChatMessage
          message={message}
          chatId={chatId}
          isStreaming={isStreaming}
          precedingUserHasAttachments={inlinePrecedingUserHasAttachments}
          hideAgentChrome={hideAgentChromeForMessage?.(message) ?? false}
          // Answerable only while the transcript ends on this turn: once the
          // person replies, their words were the answer and the form locks.
          isLastAssistant={
            onSubmitAnswers ? isLast && !isUserMessage : undefined
          }
          isQuestionFormAnswer={isQuestionFormAnswer}
          questionFormPrompts={
            isQuestionFormAnswer ? questionFormPrompts : undefined
          }
          hideAnsweredQuestionForm={hideAnsweredQuestionForm}
          onSubmitAnswers={onSubmitAnswers}
          formDisabled={formDisabled}
          formDisabledReason={formDisabledReason}
          chatLevelFileCount={chatLevelFileCount}
          presentation={presentation}
          feedActorName={feedActor ? feedActor.name : feedActorName}
          feedActorAvatar={feedActor ? feedActor.avatar : feedActorAvatar}
          feedOwnUserMessage={
            presentation === "feed" &&
            message.role === "user" &&
            isFeedMessageFromViewer(message, {
              currentUserId,
              chatOwnerId,
            })
          }
          feedMembers={feedMembers}
          collapsible={
            presentation === "feed" && message._id !== lastRenderedMessageId
          }
          feedCompactLayout={feedCompactAuthors}
          showAuthorHeader={showFeedAuthorHeader}
          renderBeforeContent={
            isUserMessage ? renderBeforeMessage?.(message) : undefined
          }
          renderAfterContent={
            isUserMessage ? renderAfterMessage?.(message) : undefined
          }
        />
        {!isUserMessage ? renderAfterMessage?.(message) : null}
      </div>,
    );
  });

  // why: in feed placeholder mode the last visible row is the empty assistant
  // shell, so its attachments are always empty. The user turn whose files the
  // agent is still reading is the last row the feed actually PAINTS: feed drops
  // every empty shell from `renderedMessages`, not just the trailing one, so
  // counting back through `visibleMessages` lands on an orphan placeholder the
  // moment a retried create_placeholder_message_activity left one behind — and
  // that orphan carries no attachments, so the "Reading attachments..." phase
  // never fires and the pre-read pause falls back to "Starting up...".
  //
  // It is the last painted USER row, not the last painted row outright: an
  // aborted ("Stopped") empty assistant row is KEPT in `renderedMessages`
  // (`isHiddenEmptyPlaceholder` bails out as soon as
  // `hideAgentChromeForMessage` is true), so it can be the last painted row
  // while this placeholder-thinking shell is still up. Reading that row's
  // always-empty attachments would put the aborted tail right back on
  // "Starting up...". Searching backwards over `renderedMessages` rather than
  // `visibleMessages` keeps the orphan skip above: an unpainted placeholder
  // between the person's turn and the shell is still never read.
  const precedingUserMessage = showFeedPlaceholderThinking
    ? findLastUserMessage(renderedMessages)
    : visibleMessages[visibleMessages.length - 1];

  const thinkingState = (
    <ThinkingState
      message={showFeedPlaceholderThinking ? lastMsg : undefined}
      precedingUserHasAttachments={!!precedingUserMessage?.attachments?.length}
    />
  );
  const thinkingRow =
    presentation === "feed" && !feedCompactAuthors ? (
      <div className="mb-4 flex gap-3" data-testid="feed-thread-thinking-row">
        <div
          className="shrink-0 pt-0.5"
          data-testid="feed-thread-thinking-avatar-column"
        >
          <PunttAvatar size={AVATAR_SIZE_BIG} />
        </div>
        <div className="min-w-0 flex-1">{thinkingState}</div>
      </div>
    ) : (
      thinkingState
    );

  return (
    <FeedCompactLayoutProvider value={feedCompactAuthors}>
      <ThreadCitationNumbersContext.Provider value={citationNumbers}>
        <div ref={columnRef} className="w-full">
          {rows}
          {showThinking ? thinkingRow : null}
          {/* why: once the assistant row exists, chat does not mount the
              generic thinking row, and a sentence above that row's tool list
              sits off the part of the thread that stays on screen. The
              sentence follows the tools for the whole wait before the card. */}
          {awaitingScope ? (
            <ThinkingState settingScope testId="ask-puntt-scope-wait" />
          ) : null}
        </div>
      </ThreadCitationNumbersContext.Provider>
    </FeedCompactLayoutProvider>
  );
}
